import { Crepe } from '@milkdown/crepe';
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

import '@milkdown/crepe/theme/common/style.css';
import '@milkdown/crepe/theme/frame.css';

/** Shape of the lazily-imported milkdownHighlight module. */
type MilkdownHighlightModule = {
  installMilkdownHighlight: (crepe: Crepe) => void;
  addHighlightTopBarItem: (builder: {
    getGroup: (key: string) => { addItem: (...args: unknown[]) => unknown };
  }) => void;
};

/** Dynamically imports the highlight plugin, returning `null` if unavailable (e.g. SSR). */
const loadMilkdownHighlightModule = async (): Promise<MilkdownHighlightModule | null> => {
  try {
    return (await import('@/components/editor/milkdownHighlight')) as MilkdownHighlightModule;
  } catch {
    return null;
  }
};

/** Registers a `markdownUpdated` listener on `crepe` that forwards updates to `onMarkdown` while the component is still mounted. */
function attachCrepeMarkdownListener(
  crepe: Crepe,
  isMounted: () => boolean,
  onMarkdown: (value: string) => void,
): void {
  crepe.on((listener) => {
    listener.markdownUpdated((_ctx, nextMarkdown) => {
      if (!isMounted()) {
        return;
      }
      onMarkdown(nextMarkdown);
    });
  });
}

/** Imperative handle exposed to parent components via `forwardRef`. */
export interface CrepeMarkdownFieldHandle {
  getMarkdown: () => string;
}

/** Props for the `CrepeMarkdownField` Milkdown Crepe editor with highlight support. */
export interface CrepeMarkdownFieldProps {
  /** Remount editor when this changes (e.g. assessment instance id). */
  bootKey: string;
  defaultMarkdown?: string;
  onMarkdownChange: (markdown: string) => void;
  /** Receives Crepe's serialized initial value without treating hydration as a user edit. */
  onReady?: (markdown: string) => void;
  minHeight?: number;
}

/**
 * Milkdown Crepe markdown editor with optional text-highlight toolbar, exposed via `forwardRef`
 * so parents can call `getMarkdown()` imperatively before saving.
 */
export const CrepeMarkdownField = forwardRef<CrepeMarkdownFieldHandle, CrepeMarkdownFieldProps>(
  function CrepeMarkdownField(
    { bootKey, defaultMarkdown = '', onMarkdownChange, onReady, minHeight = 280 },
    ref,
  ) {
    const editorHostRef = useRef<HTMLDivElement | null>(null);
    const crepeRef = useRef<Crepe | null>(null);
    const lastMarkdownRef = useRef<string>(defaultMarkdown);
    const onMarkdownChangeRef = useRef(onMarkdownChange);
    onMarkdownChangeRef.current = onMarkdownChange;
    const onReadyRef = useRef(onReady);
    onReadyRef.current = onReady;

    /** Snapshot initial markdown when `bootKey` changes only — do not remount on every `defaultMarkdown` update from parents. */
    const initialMarkdownRef = useRef(defaultMarkdown);
    const prevBootKeyRef = useRef(bootKey);
    if (prevBootKeyRef.current !== bootKey) {
      prevBootKeyRef.current = bootKey;
      initialMarkdownRef.current = defaultMarkdown;
    }

    useImperativeHandle(
      ref,
      () => ({
        getMarkdown: () => {
          try {
            return crepeRef.current?.getMarkdown() ?? lastMarkdownRef.current ?? '';
          } catch {
            return lastMarkdownRef.current ?? '';
          }
        },
      }),
      [],
    );

    useEffect(() => {
      if (!editorHostRef.current) {
        return undefined;
      }

      const initialMarkdown = initialMarkdownRef.current;
      lastMarkdownRef.current = initialMarkdown;
      let mounted = true;
      let editorReady = false;
      let localCrepe: Crepe | null = null;

      const setupEditor = async (): Promise<void> => {
        let highlightModule: MilkdownHighlightModule | null = null;
        if (globalThis.process?.env?.NODE_ENV !== 'test') {
          highlightModule = await loadMilkdownHighlightModule();
        }
        const installMilkdownHighlight = highlightModule?.installMilkdownHighlight ?? null;
        const addHighlightTopBarItem = highlightModule?.addHighlightTopBarItem ?? null;

        if (!mounted || !editorHostRef.current) {
          return;
        }

        const crepe = new Crepe({
          root: editorHostRef.current,
          defaultValue: initialMarkdown,
          features: {
            [Crepe.Feature.TopBar]: true,
          },
          featureConfigs: {
            [Crepe.Feature.Placeholder]: {
              text: "Describe goals, constraints, and priorities. Type '/' for formatting commands.",
              mode: 'block',
            },
            [Crepe.Feature.TopBar]: {
              buildTopBar: (builder) => {
                addHighlightTopBarItem?.(
                  builder as {
                    getGroup: (key: string) => { addItem: (...args: unknown[]) => unknown };
                  },
                );
              },
            },
          },
        });

        localCrepe = crepe;
        crepeRef.current = crepe;
        installMilkdownHighlight?.(crepe);

        attachCrepeMarkdownListener(
          crepe,
          () => mounted,
          (md) => {
            lastMarkdownRef.current = md;
            if (editorReady) {
              onMarkdownChangeRef.current(md);
            }
          },
        );

        await crepe.create();
        if (!mounted) {
          return;
        }
        const initial = crepe.getMarkdown();
        lastMarkdownRef.current = initial;
        editorReady = true;
        if (onReadyRef.current) {
          onReadyRef.current(initial);
        } else {
          onMarkdownChangeRef.current(initial);
        }
      };

      void setupEditor();

      return () => {
        mounted = false;
        const activeCrepe = localCrepe ?? crepeRef.current;
        crepeRef.current = null;
        if (activeCrepe) {
          void activeCrepe.destroy();
        }
      };
    }, [bootKey]);

    return (
      <div className="crepe-markdown-field" style={{ minHeight, width: '100%' }}>
        <div
          ref={editorHostRef}
          className="milkdown-host"
          aria-label="Engagement context markdown editor"
          data-testid="engagement-markdown-editor"
        />
      </div>
    );
  },
);
CrepeMarkdownField.displayName = 'CrepeMarkdownField';
